/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import { IModuleStateProps, EnabledPaymentsForOBO } from '@msdyn365-commerce-modules/checkout-utilities';
import { IModuleProps, INodeProps } from '@msdyn365-commerce-modules/utilities';
import { observer } from 'mobx-react';
import * as React from 'react';
import classname from 'classnames';

import { reaction } from 'mobx';
import { ICheckoutExpressPaymentContainerData } from './checkout-express-payment-container.data';
import {
    ICheckoutExpressPaymentContainerProps,
    ICheckoutExpressPaymentContainerHeadingData
} from './checkout-express-payment-container.props.autogenerated';
import { WaitingComponent } from '../../common/waiting';

/**
 * ICheckoutExpressPaymentContainerModuleProps interface.
 */
export interface ICheckoutExpressPaymentContainerModuleProps
    extends ICheckoutExpressPaymentContainerProps<ICheckoutExpressPaymentContainerData>,
        IModuleStateProps {}

export interface ICheckoutExpressPaymentContainerViewProps
    extends ICheckoutExpressPaymentContainerProps<{}>,
        ICheckoutExpressPaymentContainerState {
    checkoutPlainContainerProps: IModuleProps;
    items: React.ReactNode[];
    isReady?: boolean;
    waiting?: React.ReactNode;
    title?: React.ReactNode;
    headerProps: INodeProps;
}

/**
 * Checkout Express Payment Container State.
 */
export interface ICheckoutExpressPaymentContainerState {
    isReady?: boolean;
    shouldHideExpressPaymentContainer?: boolean;
}

/**
 *
 * CheckoutExpressPaymentContainer component
 * @extends {React.PureComponent<ICheckoutExpressPaymentContainerModuleProps<ICheckoutExpressPaymentContainerData>>}
 */
@observer
export class CheckoutExpressPaymentContainer extends React.PureComponent<
    ICheckoutExpressPaymentContainerProps<ICheckoutExpressPaymentContainerData>,
    ICheckoutExpressPaymentContainerState
> {
    private readonly moduleClassName: string = 'ms-checkout-express-section-container';

    public constructor(props: ICheckoutExpressPaymentContainerModuleProps) {
        super(props);
        this.state = {
            isReady: false,
            shouldHideExpressPaymentContainer: this.props.data.checkout.result?.shippingAddressFromExpressCheckout ? true : false
        };
    }

    public componentDidMount(): void {
        reaction(
            () => this.props.data.checkout.result?.isExpressPaymentModulesReady,
            () => {
                const items = this._getItems();
                if (items.length === 0) {
                    this.props.context.telemetry.error('Checkout express payment container content is empty, module wont render');
                } else {
                    let isReady = true;

                    for (const item of items) {
                        // @ts-ignore
                        if (item.key.includes('checkout-express')) {
                            // @ts-ignore
                            const extensionPropertyKey = `is${item.props.config.paymentTenderType}ExpressModuleReady`;

                            const isModuleReady =
                                this.props.data.checkout.result?.isExpressPaymentModulesReady?.find(
                                    property => property.Key === extensionPropertyKey
                                )?.Value?.BooleanValue ?? false;
                            isReady = isReady && isModuleReady;
                        }
                    }
                    this.setState({ isReady });
                }
            }
        );

        reaction(
            () => this.props.data.checkout.result?.isExpressCheckoutApplied,
            isExpressCheckoutApplied => {
                this.setState({ shouldHideExpressPaymentContainer: isExpressCheckoutApplied });
            }
        );
    }

    public render(): JSX.Element | null {
        const {
            config: { checkoutExpressPaymentContainerHeading },
            resources: { checkoutExpressPaymentSectionLoadingMessage },
            data: { checkout }
        } = this.props;

        if (!checkout.result || checkout.result?.isExpressCheckoutApplied || checkout.result?.isPaymentVerificationRedirection) {
            this.props.context.telemetry.information('Other payment is applied, checkout express payment container module wont render');
            return null;
        }

        if (this.props.context.request.user.isOBORequest && EnabledPaymentsForOBO.ExpressPayment === 0) {
            this.props.context.telemetry.information('isOBORequest is enabled, Checkout express payment container module wont render');
            return null;
        }

        const items = this._getItems();
        if (items.length === 0) {
            this.props.context.telemetry.error('Checkout express payment container content is empty, module wont render');
            return null;
        }

        const shouldHideExpressPaymentContainerClassName: string = this.state.shouldHideExpressPaymentContainer ? 'hide' : '';

        const moduleClassName = classname(
            'ms-checkout-express-payment-container',
            this.props.config.className,
            shouldHideExpressPaymentContainerClassName
        );

        const viewProps: ICheckoutExpressPaymentContainerViewProps = {
            ...this.props,

            checkoutPlainContainerProps: {
                moduleProps: this.props,
                className: moduleClassName
            },
            items: items,
            isReady: this.state.isReady,
            waiting: <WaitingComponent {...{ message: checkoutExpressPaymentSectionLoadingMessage, className: this.moduleClassName }} />,
            title: checkoutExpressPaymentContainerHeading && this._renderMsdyn365Text(checkoutExpressPaymentContainerHeading),
            headerProps: { className: 'ms-checkout-express-payment-container__head' }
        };
        return this.props.renderView(viewProps) as React.ReactElement;
    }

    private readonly _getItems = (): React.ReactNode[] => {
        const { slots } = this.props;
        return (slots && slots.primary && slots.primary.length && slots.primary) || [];
    };

    /**
     * Renders heading.
     * @param checkoutExpressPaymentContainerHeading - The checkout express container heading.
     * @returns React node.
     */
    private _renderMsdyn365Text(checkoutExpressPaymentContainerHeading: ICheckoutExpressPaymentContainerHeadingData): React.ReactNode {
        return (
            <Msdyn365.Text
                {...checkoutExpressPaymentContainerHeading}
                tag={checkoutExpressPaymentContainerHeading.tag || 'h2'}
                className='ms-checkout-express-payment-container__title'
                text={checkoutExpressPaymentContainerHeading.text}
                editProps={{ onEdit: this._handleCheckoutExpressContainerHeadingChange, requestContext: this.props.context.request }}
            />
        );
    }

    /**
     * Event handler for heading change event.
     * @param event - The content change event in heading element.
     * @returns The new heading value.
     */
    private _handleCheckoutExpressContainerHeadingChange = (event: Msdyn365.ContentEditableEvent) =>
        (this.props.config.checkoutExpressPaymentContainerHeading!.text = event.target.value);
}

export default CheckoutExpressPaymentContainer;
